/*
 * NOTICE:  All information contained herein is, and remains the property of Power Settlements Consulting and Software, LLC.
 * The intellectual and technical concepts contained herein are proprietary to Power Settlements Consulting and Software, LLC
 * and are protected by trade secret or copyright law. Dissemination or reproduction of this material, in whole or in part,
 * is strictly forbidden without the prior written permission of Power Settlements Consulting and Software, LLC.
 * Copyright (c) Power Settlements Consulting and Software, LLC. All rights reserved.
*/

import caisoUtil from '@/utils/caiso/caisoUtils';
import dateUtil from '@/utils/dateStore';
import { sortStringArrayDescending, has } from '@/utils/dataUtil';
import { getMomentDateStringFromRangeArray } from '@/utils/dateUtil';
import {
  orderCaisoStatementPublicationsDescending,
  filterAndSortCaisoStatementFileTypesDescending,
  filterAndSortCaisoStatementVersionsDescending,
} from '@/components/Ciso/BaseStore/dataUtil';
import {
  TOOLS_API, CISO_METER_API, CISO_SAMC_API, CISO_OASIS_API,
} from '@/api';
import { caiso, accessRight } from '@/auth/permission';

const selectCoordinator = (params) => params.marketParticipants
  ? params.marketParticipants.find(({ value }) => params.marketParticipantsSelected === value)
  : params.coordinatorList.find(({ value }) => params.coordinatorListSelected === value);

const selectCoordinators = (params) => {
  const selectedCoordinator = selectCoordinator(params);
  return selectedCoordinator?.value === 'ALL' ? params.coordinatorList.filter(c => c.value !== 'ALL').map(c => c.baId) : [selectedCoordinator?.baId];
}

export async function SamcTradeDateRangeSelectedHandler() {
  const [startDate, endDate] = this.params.tradeDateRangeSelected;
  const headers = await samcHeaders.fetchData({ startDate: JSON.parse(JSON.stringify(startDate)), endDate: JSON.parse(JSON.stringify(endDate)) });

  this.$store.commit('ciso/setParams', ['samcHeadersUnfiltered', headers]);
}

function caisoStatementFileVersionsHandler() {
  const selectedCoordinator = selectCoordinator(this.params);
  const selectedCoordinators = selectCoordinators(this.params);
  const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

  // Race Condition check
  if (!selectedCoordinator || !startDateCompare || !endDateCompare) { return null; }

  const filteredHeaders = this.params.samcHeaders.filter(({
    ba, tradeDate, fileType, publication, dataSource,
  }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare) && fileType === this.params.fileTypesSelected && publication === this.params.publicationsSelected && dataSource === 'SaMC');
  let orderedDescendingVersions = filterAndSortCaisoStatementVersionsDescending(filteredHeaders, { selectedCoordinator, startDateCompare, endDateCompare });

  if (selectedCoordinator.value === 'ALL' && orderedDescendingVersions.length)
    orderedDescendingVersions = [{ value: -1, label: 'MAX' }];

  const topVersion = orderedDescendingVersions.length > 0 ? orderedDescendingVersions[0] : null;

  this.$store.commit('ciso/setParams', ['fileVersions', orderedDescendingVersions]);
  this.$store.commit('ciso/setParams', ['fileVersionsSelected', topVersion ? topVersion.value : null]);
  return true;
}

function compareVersion() {
  const selectedCoordinator = selectCoordinator(this.params);
  const selectedCoordinators = selectCoordinators(this.params);
  const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

  // Race Condition check
  if (!selectedCoordinator || !startDateCompare || !endDateCompare) { return null; }

  const filteredHeaders = this.params.samcHeaders.filter(({
    ba, tradeDate, fileType, publication, dataSource,
  }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare) && fileType === this.params.compareFileTypesSelected && publication === this.params.comparePublicationsSelected && dataSource === 'SaMC');
  let orderedDescendingVersions = filterAndSortCaisoStatementVersionsDescending(filteredHeaders, { selectedCoordinator, startDateCompare, endDateCompare });

  if (selectedCoordinator.value === 'ALL' && orderedDescendingVersions.length) 
    orderedDescendingVersions = [{ value: -1, label: 'MAX' }];

  const topVersion = orderedDescendingVersions.length > 0 ? orderedDescendingVersions[0] : null;

  this.$store.commit('ciso/setParams', ['compareVersions', orderedDescendingVersions]);
  this.$store.commit('ciso/setParams', ['compareVersionsSelected', topVersion ? topVersion.value : null]);
  return true;
}

function publicationHandler() {
  const selectedCoordinators = selectCoordinators(this.params);
  const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

  // Race Condition check
  if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }
  const filteredHeaders = this.params.samcHeaders.filter(({ ba, tradeDate, fileType }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare) && fileType === this.params.fileTypesSelected);
  const filteredPublications = filteredHeaders.map((x) => x.publication).filter((x, i, a) => a.indexOf(x) === i).map((f) => ({ value: f, label: f }));
  const orderedDescendingPublications = orderCaisoStatementPublicationsDescending(filteredPublications);

  const topPub = orderedDescendingPublications.length > 0 ? orderedDescendingPublications[0] : null;
  this.$store.commit('ciso/setParams', ['publications', orderedDescendingPublications]);
  this.$store.commit('ciso/setParams', ['publicationsSelected', null]);
  this.$nextTick(() => this.$store.commit('ciso/setParams', ['publicationsSelected', topPub ? topPub.value : null]));
  return true;
}

function comparePubHandler() {
  const selectedCoordinators = selectCoordinators(this.params);
  const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

  // Race Condition check
  if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }
  const filteredHeaders = this.params.samcHeaders.filter(({ ba, tradeDate, fileType }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare) && fileType === this.params.compareFileTypesSelected);
  const filteredPublications = filteredHeaders.map((x) => x.publication).filter((x, i, a) => a.indexOf(x) === i).map((f) => ({ value: f, label: f }));
  const orderedDescendingPublications = orderCaisoStatementPublicationsDescending(filteredPublications);

  const topPub = orderedDescendingPublications.length > 0 ? orderedDescendingPublications[0] : null;
  this.$store.commit('ciso/setParams', ['comparePublications', orderedDescendingPublications]);
  this.$store.commit('ciso/setParams', ['comparePublicationsSelected', null]);
  this.$nextTick(() => this.$store.commit('ciso/setParams', ['comparePublicationsSelected', topPub ? topPub.value : null]));
  return true;
}

export const gridConfig = {
  valFormat: '#,##0.####',
  valFormat2: '#,##0.##',
  decimalFormat: '#,##0.00',
  currencyFormat: (value) => {
    if (value === null || parseFloat(value, 10) === 0) { return null; }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return formatter.format(value);
  },
};

export const samcHeaders = {
  visible: false,
  type: 'select',
  label: 'samcHeader',
  permission: { name: caiso.samc.statement, right: [accessRight.read, accessRight.write] },
  value: null,
  options: [],
  async fetchData(params) {
    try {
      if (typeof params === 'undefined') {
        const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(
          dateUtil.getDefaultRange(),
        );
        params = {
          startDate: JSON.parse(JSON.stringify(startDateCompare)),
          endDate: JSON.parse(JSON.stringify(endDateCompare)),
        };
      }
      const { data: { data } } = await CISO_SAMC_API.get('/statements/headers', {
        params,
      });
      
      data.forEach((d, idx) => {
        d.value = idx;
      });

      return data;
    } catch (error) {
      vue.$notify('Failed to Load Samc Headers', 'error');
      console.log(error);
    }
    return [];
  },
};

export const eimHeaders = {
  visible: false,
  type: 'select',
  label: 'EIM Headers',
  permission: { name: caiso.samc.eimSubAllocation, right: [accessRight.read, accessRight.write] },
  value: null,
  options: [],
  async fetchData() {
    try {
      const { data } = await CISO_SAMC_API.get('/eim-sub-allocation/header');
      if (data) {
        data.forEach((d, idx) => {
          d.value = idx;
        });
        return data;
      }
      return [{}];
    } catch (error) {
      vue.$notify('Failed to Load EIM Sub Allocation Headers', 'error');
    }
    return [{}];
  },
};

export const stateHeaders = {
  visible: false,
  type: 'select',
  label: 'State Alloc Headers',
  permission: { name: caiso.samc.eimSubAllocation, right: [accessRight.read, accessRight.write] },
  value: null,
  options: [],
  async fetchData() {
    try {
      const { data } = await CISO_SAMC_API.get('/state-alloc-ref/header');
      if (data) {
        data.forEach((d, idx) => {
          d.value = idx;
        });

        return data;
      }
      return [{}];
    } catch (error) {
      vue.$notify('Failed to State Allocation Headers', 'error');
    }
    return [{}];
  },
};

export const marketParticipants = {
  label: 'Company',
  value: (() => caisoUtil.coordinator),
  type: 'tagbox',
  hasAll: false,
  showDropDownButton: true,
  visible: true,
  searchable: true,
  options: [],
  async fetchData() {
    try {
      const { data: { schedulingCoordinators } } = await TOOLS_API.get('/scheduling-coordinators');
      const data = caisoUtil.configureCoordinators(schedulingCoordinators);
      if (this.hasAll) {
        data.unshift({ value: 'ALL', label: 'ALL' });
      }
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
};

export const coordinatorList = {
  label: 'Company',
  value: (() => caisoUtil.coordinator),
  type: 'select',
  hasAll: false,
  visible: true,
  async fetchData() {
    try {
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/resources');
      const data = caisoUtil.configureCoordinators(scs);
      if (this.hasAll) {
        data.unshift({ value: 'ALL', label: 'ALL' });
      }
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
  options: [],
};

export const meterRsrcScList = {
  label: 'Meter Resources',
  options: [],
  value: null,
  type: 'select',
  hasAll: false,
  removeDuplicates: false,
  visible: false,
  async fetchData() {
    try {
      const params = { removeDuplicates: this.removeDuplicates };
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/meter-resources', { params });
      const data = caisoUtil.configureCoordinatorsMeter(scs);
      if (this.hasAll) {
        data.unshift({ value: 'ALL', label: 'ALL' });
      }
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
};

export const schedulingCoordinatorsAndEimEntitiesWithTagbox = {
  label: 'Companies',
  value: (() => caisoUtil.coordinator ? [caisoUtil.coordinator] : null),
  type: 'tagbox',
  visible: true,
  options: [],
  searchable: true,
  async fetchData() {
    try {
      const scList = [];
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/resources');
      const { data: { schedulingCoordinators: ents } } = await TOOLS_API.get('/scheduling-coordinators/eim-entities');

      scList.push(...scs);
      scList.push(...ents);

      const data = caisoUtil.configureCoordinators(scList);
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'marketParticipants',
    handler(newValue, oldValue) {
      if (this.params.coordinatorListUnfiltered && this.params.coordinatorListUnfiltered.length > 0) {
        const mappedCoordinators = caisoUtil.mapCoordinatorList(this.params.coordinatorListUnfiltered);
        this.$store.commit('ciso/setParams', ['coordinatorList', mappedCoordinators]);
        this.$store.commit('ciso/setParams', ['coordinatorListSelected', (caisoUtil.coordinator ? [caisoUtil.coordinator] : [])]);
      }
    },
  }],
};

export const schedulingCoordinatorsWithTagbox = {
  label: 'Companies',
  value: (() => caisoUtil.coordinator ? [caisoUtil.coordinator] : null),
  type: 'tagbox',
  visible: true,
  required: true,
  options: [],
  searchable: true,
  async fetchData() {
    try {
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/resources');
      const data = caisoUtil.configureCoordinators(scs);
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'marketParticipants',
    handler(newValue, oldValue) {
      if (this.params.coordinatorListUnfiltered && this.params.coordinatorListUnfiltered.length > 0) {
        const mappedCoordinators = caisoUtil.mapCoordinatorList(this.params.coordinatorListUnfiltered);
        this.$store.commit('ciso/setParams', ['coordinatorList', mappedCoordinators]);
        this.$store.commit('ciso/setParams', ['coordinatorListSelected', (caisoUtil.coordinator ? [caisoUtil.coordinator] : [])]);
      }
    },
  }],
};

export const eimEntityCoordinatorListWithTagbox = {
  label: 'Companies',
  value: (() => caisoUtil.eimEntity ? [caisoUtil.eimEntity] : null),
  type: 'tagbox',
  visible: true,
  options: [],
  searchable: true,
  async fetchData() {
    try {
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/eim-entities');
      const data = caisoUtil.configureCoordinators(scs);
      return data;
    } catch (error) {
      vue.$notify('Failed to load EIM Entities', 'error');
      console.log(error);
    }
    return [];
  },
};

export const dateType = {
  label: 'Date Type',
  value: 'Trade',
  type: 'radio',
  options: [{ value: 'Trade', label: 'Trade Date' }, { value: 'Published', label: 'Publish Date' }],
};

export const resourceType = {
  label: 'Resource Type',
  value: 'ALL',
  type: 'select',
  clearable: true,
  options: [
    'ALL',
    'GEN',
    'LOAD',
    'ITIE',
    'ETIE',
    'TIE',
  ].map((value) => ({ value, label: value })),
};

export const schedulingCoordinatorsWithSubCompany = {
  label: 'Company',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  async fetchData() {
    try {
      const { data: { schedulingCoordinators: scs } } = await TOOLS_API.get('/scheduling-coordinators/resources');
      const data = caisoUtil.configureCoordinators(scs);
      return data;
    } catch (error) {
      vue.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'marketParticipants',
    handler(newValue, oldValue) {
      const mappedCoordinators = caisoUtil.mapCoordinatorList(newValue);
      this.$store.commit('ciso/setParams', ['coordinatorList', mappedCoordinators]);
      this.$store.commit('ciso/setParams', ['coordinatorListSelected', caisoUtil.coordinator]);
      if (this.params.subCompanyList && this.params.subCompanyList.length > 0) {
        this.$store.commit('ciso/setParams', ['subCompanyListSelected', this.params.subCompanyList[0].value]);
      }
    },
  }],
};

export const sourceSystem = {
  label: 'Source System',
  value: [],
  type: 'tagbox',
  options: [],
  async fetchData() {
    try {
      const { data } = await TOOLS_API.get('/server-queue-parent/source-system');
      return data.map((item) => (item.sourceSystem)).sort();
    } catch (error) {
      vue.$notify('Failed to load Source Systems', 'error');
      console.error(error);
    }
    return [];
  },
};

export const processStatus = {
  label: 'Process Status',
  value: [],
  type: 'tagbox',
  options: [
    'Queued',
    'Downloading',
    'UnZipping',
    'Waiting',
    'Importing',
    'Processing',
    'Cancelled',
    'Errored',
    'Completed',
    'Warning',
  ].sort((a, b) => (a < b) ? -1 : 1).map((value) => ({ value, label: value })),
};

export const balancingAreaAuthority = {
  label: 'Balancing Authority Area',
  value: 'ALL',
  type: 'select',
  includeAllOption: true,
  options: [],
  async fetchData() {
    try {
      const data = await TOOLS_API.get('/balancing-authority-areas');
      const BalanceAuthorityAreas = data.data.data
        .map((item) => (item.baa));
      if (this.includeAllOption) BalanceAuthorityAreas.unshift('ALL');
      return BalanceAuthorityAreas;
    } catch (error) {
      vue.$notify('Failed to load Balance authority area', 'error');
      console.error(error);
    }
    return [];
  },
};

export const oasisReportList = {
  label: 'Report',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  async fetchData() {
    try {
      const { data } = await CISO_OASIS_API.get('/oasis-reports');
      const reports = caisoUtil.getOasisReportOptions(data);
      return reports;
    } catch (error) {
      vue.$notify('Failed to load Oasis Reports', 'error');
      console.log(error);
    }
    return [];
  },
};

export const processDateType = {
  label: 'Date Type',
  value: 'True',
  type: 'radio',
  options: [{ value: 'True', label: 'Process Date' }, { value: 'False', label: 'Trade Date' }],
};

export const tradeDate = {
  label: 'Trade Date',
  value: (() => dateUtil.getDefaultDate().toISOString()),
  type: 'date',
};

export const tradeDateRange = {
  label: 'Date',
  value: (() => dateUtil.getDefaultRange()),
  type: 'dateRange',
};

export const resources = {
  label: 'Resource ID',
  value: null,
  type: 'select',
  searchable: true,
  options: [],
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      const resourceList = caisoUtil.getSchedulingCoordinatorResourcesOptions(this.params.coordinatorListUnfiltered, newValue);

      this.$store.commit('ciso/setParams', ['resourceList', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListUnfiltered', resourceList]);
    },
  }],
};

export const resourcesTagboxEtsr = {
  label: 'Resource ID(s)',
  value: [],
  type: 'tagbox',
  searchable: true,
  showDropDownButton: false,
  showClearButton: true,
  options: [],
  disabled: false,
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.config && this.config.resourceList) {
        this.config.resourceList.disabled = (newValue === 'ALL');
      }

      const resourceListFull = caisoUtil
        .getSchedulingCoordinatorResourcesOptions(this.params.coordinatorListUnfiltered, newValue);
      const resourceList = resourceListFull.filter((x) => x.shortName.includes('EIMBASE'));

      this.$store.commit('ciso/setParams', ['resourceList', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListUnfiltered', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListSelected', []]);
    },
  },
  {
    propertyToWatch: 'resourceListSelected',
    handler(newValue, oldValue) {
      if (this.$parent.instance && this.$parent.instance.NAME === 'dxPopup') {
        this.$nextTick(() => this.$parent.instance.repaint());
      }
    },
  }],
};

export const resourcesTagboxMeter = {
  label: 'Resource ID(s)',
  value: [],
  type: 'tagbox',
  searchable: true,
  showDropDownButton: false,
  showClearButton: true,
  options: [],
  disabled: false,
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.config && this.config.resourceList) {
        this.config.resourceList.disabled = (newValue === 'ALL');
      }

      const resourceList = caisoUtil
        .getSchedulingCoordinatorMeterResourcesOptions(this.params.meterRsrcScListUnfiltered, newValue);

      this.$store.commit('ciso/setParams', ['resourceList', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListUnfiltered', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListSelected', []]);
    },
  },
  {
    propertyToWatch: 'resourceListSelected',
    handler(newValue, oldValue) {
      if (this.$parent.instance && this.$parent.instance.NAME === 'dxPopup') {
        this.$nextTick(() => this.$parent.instance.repaint());
      }
    },
  }],
};

export const resourcesTagbox = {
  label: 'Resource ID(s)',
  value: [],
  type: 'tagbox',
  searchable: true,
  showDropDownButton: false,
  showClearButton: true,
  options: [],
  disabled: false,
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.config && this.config.resourceList) {
        this.config.resourceList.disabled = (newValue === 'ALL');
      }

      const resourceList = caisoUtil
        .getSchedulingCoordinatorResourcesOptions(this.params.coordinatorListUnfiltered, newValue);

      this.$store.commit('ciso/setParams', ['resourceList', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListUnfiltered', resourceList]);
      this.$store.commit('ciso/setParams', ['resourceListSelected', []]);
    },
  },
  {
    propertyToWatch: 'resourceListSelected',
    handler(newValue, oldValue) {
      if (this.$parent.instance && this.$parent.instance.NAME === 'dxPopup') {
        this.$nextTick(() => this.$parent.instance.repaint());
      }
    },
  }],
};

export const subCompanyList = {
  label: 'Sub Company',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  clearable: true,
  async fetchData() {
    try {
      const { data: { data } } = await TOOLS_API.get('/transmission-billing-info/sub-companies');
      return data.map(({ subCompany, sc }) => ({ value: subCompany, label: subCompany, sc })).filter((value, index, self) => self.map((v) => v.value).indexOf(value.value) === index);
    } catch (error) {
      vue.$notify('Failed to load Trans Billing Sub Companies', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.params.coordinatorListSelected && this.params.coordinatorListSelected !== null) {
        this.$store.commit('ciso/setParams', ['subCompanyList', this.params.subCompanyListUnfiltered.filter((x) => x.sc === this.params.coordinatorListSelected)]);
        // if (this.params.subCompanyList && this.params.subCompanyList.length > 0) {
        //   this.$store.commit('ciso/setParams', ['subCompanyListSelected', this.params.subCompanyList[0].value]);
        // }
      } else {
        this.$store.commit('ciso/setParams', ['subCompanyList', this.params.subCompanyListUnfiltered]);
        // if (this.params.subCompanyList && this.params.subCompanyList.length > 0) {
        //   this.$store.commit('ciso/setParams', ['subCompanyListSelected', this.params.subCompanyList[0].value]);
        // }
      }
    },
  }],
};

export const meterSubmitUnitList = {
  label: 'RSRC_ID',
  value: [],
  type: 'tagbox',
  searchable: true,
  showDropDownButton: false,
  disabled: false,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_METER_API.get('/mris-submission/mapping');
      return data.map(({ resourceId, scid }) => ({ value: resourceId, label: resourceId, scid }))
        .filter((value, index, self) => self.map((v) => v.value)
          .indexOf(value.value) === index);
    } catch (error) {
      vue.$notify('Failed to load Meter Submit Units', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.params.coordinatorListSelected && this.params.coordinatorListSelected !== null) {
        this.$store.commit('ciso/setParams', ['meterSubmitUnitList', this.params.meterSubmitUnitListUnfiltered
          .filter((x) => x.scid === this.params.coordinatorListSelected)]);
      } else {
        this.$store.commit('ciso/setParams', ['meterSubmitUnitList', this.params.meterSubmitUnitListUnfiltered]);
      }
    },
  }],
};

export const marketTypes = {
  label: 'Market Type',
  value: (() => caisoUtil.marketType),
  type: 'select',
  options: caisoUtil.marketTypeOptions,
  clearable: true,
};

export const executionTypes = {
  label: 'Execution Type',
  value: '',
  type: 'select',
  options: [],
  watchers: [{
    propertyToWatch: 'marketTypeListSelected',
    handler(newValue, oldValue) {
      const executionTypeList = caisoUtil.getExecutionTypesOptions(this.params.marketTypeList, newValue);
      this.$store.commit('ciso/setParams', ['executionTypeList', executionTypeList]);
    },
  }],
};

export const filteredExecutionTypes = {
  label: 'Execution Type',
  value: '',
  type: 'select',
  options: [],
  watchers: [{
    propertyToWatch: 'marketTypeListSelected',
    handler(newValue, oldValue) {
      const executionTypeList = caisoUtil.getExecutionTypesOptions(this.params.marketTypeList, newValue).filter((_, index) => index !== 3); // excludes RTPD execution type
      this.$store.commit('ciso/setParams', ['executionTypeList', executionTypeList]);
    },
  }],
};

export const chargeCodes = {
  label: 'Charge Code',
  value: null,
  type: 'select',
  searchable: true,
  permission: { name: caiso.samc.statement, right: [accessRight.read, accessRight.write] },
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/statements/charge-types');
      if (this.hasAll) data.unshift({ chargeType: 'ALL' });
      return data.map(({ chargeType }) => ({ value: chargeType, label: chargeType }));
    } catch (error) {
      vue.$notify('Failed to load Charge Codes', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'marketParticipants',
    handler(newValue, oldValue) {
      if (this.params.chargeCodes && this.params.chargeCodes.length > 0) {
        this.$store.commit('ciso/setParams', ['charges', chargeCodes]);
      }
    },
  }],
};

export const chargeCodesTagbox = {
  label: 'Charge Code(s)',
  value: null,
  type: 'tagbox',
  searchable: true,
  permission: { name: caiso.samc.statement, right: [accessRight.read, accessRight.write] },
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/statements/charge-types');
      return data.map(({ chargeType }) => ({ value: chargeType, label: chargeType }));
    } catch (error) {
      vue.$notify('Failed to load Charge Codes', 'error');
      console.log(error);
    }
    return [];
  },
};

export const eimChargeCodes = {
  label: 'Charge Code',
  value: null,
  type: 'select',
  searchable: true,
  permission: { name: caiso.samc.eimSubAllocation, right: [accessRight.read, accessRight.write] },
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/eim-sub-allocation/charge-codes');
      const chargeCodes = data.map((x) => x.name);
      return chargeCodes;
    } catch (error) {
      vue.$notify('Failed to load Charge Codes', 'error');
      console.log(error);
    }
    return [];
  },
};

export const billDeterminants = {
  label: 'Bill Determinants',
  value: null,
  type: 'select',
  searchable: true,
  permission: { name: caiso.samc.statement, right: [accessRight.read, accessRight.write] },
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/statements/bill-determinants');
      return data.map(({ billDeterminant }) => ({ value: billDeterminant, label: billDeterminant }));
    } catch (error) {
      vue.$notify('Failed to load Bill Determinants', 'error');
      console.log(error);
    }
    return [];
  },
  watchers: [{
    propertyToWatch: 'marketParticipants',
    handler(newValue, oldValue) {
      if (this.params.chargeCodes && this.params.chargeCodes.length > 0) {
        this.$store.commit('ciso/setParams', ['charges', chargeCodes]);
      }
    },
  }],
};

export const bdGroup = {
  visible: true,
  type: 'tagbox',
  label: 'BD Group',
  value: null,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/charge-code-categories/bd-group');
      return data.map(({ bdGroup }) => ({ label: bdGroup, value: bdGroup }));
    } catch (error) {
      vue.$notify('Failed to Load BD Group', 'error');
      console.error(error);
    }
    return [];
  },
};

export const chargeCodesWithPreCalc = {
  label: 'Charge Code',
  value: null,
  type: 'select',
  searchable: true,
  required: true,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/configuration-file/charge-types');
      return data.map(({ chargeType }) => ({ value: chargeType, label: chargeType }));
    } catch (error) {
      vue.$notify('Failed to load Charge Codes', 'error');
      console.log(error);
    }
    return [];
  },
};

export const configurationFileHeaders = {
  label: 'Source',
  value: null,
  type: 'select',
  required: true,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/configuration-file/headers');
      return data.map(({ confImportId, confSource, timeDate }) => ({ value: { confImportId, confSource, timeDate }, label: confSource }));
    } catch (error) {
      vue.$notify('Failed to load Charge Codes', 'error');
      console.log(error);
    }
    return [];
  },
};

export const stateAllocYears = {
  label: 'Year',
  value: '-1',
  type: 'select',
  searchable: true,
  options: [],
  watchers: [
    {
      propertyToWatch: 'allocationTypeSelected',
      handler(allocationType, oldValue) {
        this.config.stateAllocYears.disabled = (allocationType === 'JLS' || allocationType === 'SCHEDULE');
        this.config.tradeDateRange.disabled = !(allocationType === 'JLS' || allocationType === 'SCHEDULE');
      },
    }],
  async fetchData() {
    try {
      const { data } = await CISO_SAMC_API.get('/state-alloc-ref/years', { });

      const yearOptions = data.map((y) => ({ value: y, label: y }));

      yearOptions.unshift({ value: '-1', label: 'ALL' });
      return yearOptions;
    } catch (error) {
      vue.$notify('Failed to load years', 'error');
      console.log(error);
    }
    return [];
  },
};

export const extractFileType = {
  label: 'Extract Type',
  value: 'EXCEL',
  type: 'select',
  searchable: true,
  options: [{
    label: 'EXCEL', value: 'EXCEL',
  }, {
    label: 'CSV', value: 'CSV',
  }],
};

export const stmtPubDates = {
  visible: false,
  type: 'select',
  label: 'stmtPubDates',
  value: null,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/statements/stmt-publication-dates');
      data.forEach((d, idx) => {
        d.value = idx;
      });
      return data;
    } catch (error) {
      vue.$notify('Failed to Load Statement Publication Dates', 'error');
      console.error(error);
    }
    return [];
  },
};

export const disputeDateType = {
  label: 'Date Type',
  value: 'Trade Date',
  type: 'radio',
  options: [{ value: 'Trade Date', label: 'Trade Date' }, { value: 'Created Date', label: 'Created Date' }, { value: 'Updated Date', label: 'Updated Date' }],
};

export const caisoStatementFileTypes = {
  label: 'File Type',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  watchers: [{
    propertyToWatch: 'samcHeaders',
    handler(newValue, oldValue) {
      const selectedCoordinators = selectCoordinators(this.params);
      const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

      // Race Condition check
      if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

      const filteredHeaders = this.params.samcHeaders.filter(({
        ba, tradeDate, fileType,
      }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
      const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

      this.$store.commit('ciso/setParams', ['fileTypes', orderedDescendingFileTypes]);
      const statementDailyFileTypes = orderedDescendingFileTypes.filter((x) => x.value.includes('DAILY'));
      const topFileType = statementDailyFileTypes.length > 0 ? statementDailyFileTypes[0] : null;
      this.$store.commit('ciso/setParams', ['fileTypesSelected', topFileType ? topFileType.value : null]);
      return true;
    },
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    async handler(newValue, oldValue) {
      const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);
      try {
        if (!has(this.$parent.$slots, 'dock')) {
          this.params.samcHeaders = await samcHeaders
            .fetchData({
              startDate: JSON.parse(JSON.stringify(startDateCompare)),
              endDate: JSON.parse(JSON.stringify(endDateCompare)),
            });
        }
      } catch (error) {
        vue.$notify('Failed to load SaMC Headers', 'error');
        console.log(error);
      }

      const selectedCoordinators = selectCoordinators(this.params);

      // Race Condition check
      if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

      const filteredHeaders = this.params.samcHeaders.filter(({
        ba, tradeDate, fileType,
      }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
      const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

      this.$store.commit('ciso/setParams', ['fileTypes', orderedDescendingFileTypes]);
      const statementDailyFileTypes = orderedDescendingFileTypes.filter((x) => x.value.includes('DAILY'));
      const topFileType = statementDailyFileTypes.length > 0 ? statementDailyFileTypes[0] : null;
      this.$store.commit('ciso/setParams', ['fileTypesSelected', topFileType ? topFileType.value : null]);
      return true;
    },
  }, {
    propertyToWatch: 'marketParticipantsSelected',
    handler(newValue, oldValue) {
      const selectedCoordinator = this.params.marketParticipants.find(({ value }) => this.params.marketParticipantsSelected === value);
      const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);
      // Race Condition check
      if (!selectedCoordinator || !startDateCompare || !endDateCompare) { return null; }

      const filteredHeaders = this.params.samcHeaders.filter(({
        ba, tradeDate, fileType,
      }) => ba === selectedCoordinator.baId && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
      const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

      this.$store.commit('ciso/setParams', ['fileTypes', orderedDescendingFileTypes]);
      const statementDailyFileTypes = orderedDescendingFileTypes.filter((x) => x.value.includes('DAILY'));
      const topFileType = statementDailyFileTypes.length > 0 ? statementDailyFileTypes[0] : null;
      this.$store.commit('ciso/setParams', ['fileTypesSelected', topFileType ? topFileType.value : null]);
      return true;
    },
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      const selectedCoordinators = selectCoordinators(this.params);
      const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);
      // Race Condition check
      if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

      const filteredHeaders = this.params.samcHeaders.filter(({
        ba, tradeDate, fileType,
      }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
      const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

      this.$store.commit('ciso/setParams', ['fileTypes', orderedDescendingFileTypes]);
      const statementDailyFileTypes = orderedDescendingFileTypes.filter((x) => x.value.includes('DAILY'));
      const topFileType = statementDailyFileTypes.length > 0 ? statementDailyFileTypes[0] : null;
      this.$store.commit('ciso/setParams', ['fileTypesSelected', topFileType ? topFileType.value : null]);
      return true;
    },
  }],
};

export const caisoStatementPublications = {
  label: 'Publication',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  watchers: [{
    propertyToWatch: 'fileTypesSelected',
    handler: publicationHandler,
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    handler: publicationHandler,
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler: publicationHandler,
  },
  ],
};

export const caisoStatementFileVersions = {
  label: 'Version',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  watchers: [{
    propertyToWatch: 'publicationsSelected',
    handler: caisoStatementFileVersionsHandler,
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    handler: caisoStatementFileVersionsHandler,
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler: caisoStatementFileVersionsHandler,
  }],
};

export const caisoStatementHasCompare = {
  label: 'Compare',
  value: false,
  type: 'checkbox',
  visible: true,
  options: [],
  watchers: [{
    propertyToWatch: 'hasCompareSelected',
    handler(newValue, oldValue) {
      if (this.$parent.instance && this.$parent.instance.NAME === 'dxPopup') {
        this.$nextTick(() => this.$parent.instance.repaint());
      }
      return true;
    },
  }],
};

export const caisoStatementCompareFileTypes = {
  label: 'Compare File Type',
  value: null,
  type: 'select',
  visible: 'hasCompare',
  options: [],
  watchers: [{
    propertyToWatch: 'hasCompareSelected',
    handler(newValue, oldValue) {
      if (newValue) {
        const selectedCoordinators = selectCoordinators(this.params);
        const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

        // Race Condition check
        if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

        const filteredHeaders = this.params.samcHeaders.filter(({
          ba, tradeDate, fileType,
        }) =>  selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
        const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

        const shadowStatementFileTypes = orderedDescendingFileTypes.filter((fileType) => fileType.value.includes('SHADOW'));
        let orderedDescendingShadowFileTypes = [];
        if (shadowStatementFileTypes && shadowStatementFileTypes !== null && shadowStatementFileTypes.length > 0) {
          orderedDescendingShadowFileTypes = sortStringArrayDescending(shadowStatementFileTypes);
        }
        const topCompareFileType = orderedDescendingShadowFileTypes.length > 0 ? orderedDescendingShadowFileTypes[0] : orderedDescendingFileTypes[0];
        this.$store.commit('ciso/setParams', ['compareFileTypes', orderedDescendingFileTypes]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
        this.$nextTick(() => this.$store.commit('ciso/setParams', ['compareFileTypesSelected', topCompareFileType ? topCompareFileType.value : null]));
      } else {
        this.$store.commit('ciso/setParams', ['compareFileTypes', []]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
      }
      return true;
    },
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    async handler(newValue, oldValue) {
      if (this.params.hasCompareSelected) {
        const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);
        try {
          if (!has(this.$parent.$slots, 'dock')) {
            this.params.samcHeaders = await samcHeaders
              .fetchData({
                startDate: JSON.parse(JSON.stringify(startDateCompare)),
                endDate: JSON.parse(JSON.stringify(endDateCompare)),
              });
          }
        } catch (error) {
          vue.$notify('Failed to load SaMC Headers', 'error');
          console.log(error);
        }

        const selectedCoordinators = selectCoordinators(this.params);

        // Race Condition check
        if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

        const filteredHeaders = this.params.samcHeaders.filter(({
          ba, tradeDate, fileType,
        }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
        const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

        const shadowStatementFileTypes = orderedDescendingFileTypes.filter((fileType) => fileType.value.includes('SHADOW'));
        let orderedDescendingShadowFileTypes = [];
        if (shadowStatementFileTypes && shadowStatementFileTypes !== null && shadowStatementFileTypes.length > 0) {
          orderedDescendingShadowFileTypes = sortStringArrayDescending(shadowStatementFileTypes);
        }
        const topCompareFileType = orderedDescendingShadowFileTypes.length > 0 ? orderedDescendingShadowFileTypes[0] : orderedDescendingFileTypes[0];
        this.$store.commit('ciso/setParams', ['compareFileTypes', orderedDescendingFileTypes]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
        this.$nextTick(() => this.$store.commit('ciso/setParams', ['compareFileTypesSelected', topCompareFileType ? topCompareFileType.value : null]));
      } else {
        this.$store.commit('ciso/setParams', ['compareFileTypes', []]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
      }
      return true;
    },
  }, {
    propertyToWatch: 'marketParticipantsSelected',
    handler(newValue, oldValue) {
      if (this.params.hasCompareSelected) {
        const selectedCoordinator = this.params.marketParticipants.find(({ value }) => this.params.marketParticipantsSelected === value);
        const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

        // Race Condition check
        if (!selectedCoordinator || !startDateCompare || !endDateCompare) { return null; }

        const filteredHeaders = this.params.samcHeaders.filter(({
          ba, tradeDate, fileType,
        }) => ba === selectedCoordinator.baId && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
        const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

        const shadowStatementFileTypes = orderedDescendingFileTypes.filter((fileType) => fileType.value.includes('SHADOW'));
        let orderedDescendingShadowFileTypes = [];
        if (shadowStatementFileTypes && shadowStatementFileTypes !== null && shadowStatementFileTypes.length > 0) {
          orderedDescendingShadowFileTypes = sortStringArrayDescending(shadowStatementFileTypes);
        }
        const topCompareFileType = orderedDescendingShadowFileTypes.length > 0 ? orderedDescendingShadowFileTypes[0] : orderedDescendingFileTypes[0];
        this.$store.commit('ciso/setParams', ['compareFileTypes', orderedDescendingFileTypes]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
        this.$nextTick(() => this.$store.commit('ciso/setParams', ['compareFileTypesSelected', topCompareFileType ? topCompareFileType.value : null]));
      } else {
        this.$store.commit('ciso/setParams', ['compareFileTypes', []]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
      }
      return true;
    },
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler(newValue, oldValue) {
      if (this.params.hasCompareSelected) {
        this.params.hasCompareSelected = false;
        const selectedCoordinators = selectCoordinators(this.params);
        
        const { startDate: startDateCompare, endDate: endDateCompare } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);

        // Race Condition check
        if (!selectedCoordinators[0] || !startDateCompare || !endDateCompare) { return null; }

        const filteredHeaders = this.params.samcHeaders.filter(({
          ba, tradeDate, fileType,
        }) => selectedCoordinators.includes(ba) && (tradeDate.split('T')[0] >= startDateCompare && tradeDate.split('T')[0] <= endDateCompare));
        const orderedDescendingFileTypes = filterAndSortCaisoStatementFileTypesDescending(filteredHeaders);

        const shadowStatementFileTypes = orderedDescendingFileTypes.filter((fileType) => fileType.value.includes('SHADOW'));
        let orderedDescendingShadowFileTypes = [];
        if (shadowStatementFileTypes && shadowStatementFileTypes !== null && shadowStatementFileTypes.length > 0) {
          orderedDescendingShadowFileTypes = sortStringArrayDescending(shadowStatementFileTypes);
        }
        const topCompareFileType = orderedDescendingShadowFileTypes.length > 0 ? orderedDescendingShadowFileTypes[0] : orderedDescendingFileTypes[0];
        this.$store.commit('ciso/setParams', ['compareFileTypes', []]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
        this.$nextTick(() => {
          this.$store.commit('ciso/setParams', ['compareFileTypesSelected', topCompareFileType ? topCompareFileType.value : null]);
          this.params.hasCompareSelected = true;
        });
      } else {
        this.$store.commit('ciso/setParams', ['compareFileTypes', []]);
        this.$store.commit('ciso/setParams', ['compareFileTypesSelected', null]);
      }
      return true;
    },
  }],
};

export const caisoStatementComparePublications = {
  label: 'Compare Publication',
  value: null,
  type: 'select',
  visible: 'hasCompare',
  options: [],
  watchers: [{
    propertyToWatch: 'compareFileTypesSelected',
    handler: comparePubHandler,
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    handler: comparePubHandler,
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler: comparePubHandler,
  }],
};

export const caisoStatementCompareFileVersions = {
  label: 'Compare Version',
  value: null,
  type: 'select',
  visible: 'hasCompare',
  options: [],
  watchers: [{
    propertyToWatch: 'comparePublicationsSelected',
    handler: compareVersion,
  }, {
    propertyToWatch: 'tradeDateRangeSelected',
    handler: compareVersion,
  }, {
    propertyToWatch: 'coordinatorListSelected',
    handler: compareVersion,
  }],
};

export const baseSchedulesTMax = {
  label: 'View max publication only (T-MAX)',
  value: false,
  type: 'checkbox',
  visible: true,
  options: [],
};

export const baseSchedulesOptions = {
  label: 'Publications',
  value: ['T-75m', 'T-55m', 'T-40m'],
  type: 'tagbox',
  visible: true,
  disabled: false,
  watchers: [{
    propertyToWatch: 'baseSchedulesTMaxSelected',
    handler(isTMaxSelected) {
      this.config.baseSchedulesOptions.disabled = isTMaxSelected;
    },
  }],
  options: ['T-7CD 9:30am',
    'T-7CD 10:05am',
    'T-75m',
    'T-55m',
    'T-40m'],
};

export const overrideHeaders = {
  label: 'overrideHeaders',
  value: null,
  type: 'select',
  visible: false,
  options: [],
  async fetchData() {
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/eim-sub-allocation/override-headers');
      data.forEach((d, idx) => {
        d.value = idx;
      });
      return data;
    } catch (error) {
      vue.$notify('Failed to Load Override Headers', 'error');
      console.log(error);
    }
    return [];
  },
};

function templateOverrideVersionsHandler() {
  const selectedCoordinator = this.params.marketParticipantsSelected || [];
  const { startDate, endDate } = getMomentDateStringFromRangeArray(this.params.tradeDateRangeSelected);
  if (!startDate || !endDate) { return null; }
  if (selectedCoordinator && selectedCoordinator.length === 0) {
    this.$store.commit('ciso/setParams', ['templateOverrideVersions', [{ value: -1, label: 'MAX' }]]);
    this.$store.commit('ciso/setParams', ['templateOverrideVersionsSelected', -1]);
    return true;
  }

  const filteredOverrideHeaders = this.params.overrideHeaders.filter(({
    tradingDate,
    scid,
  }) => selectedCoordinator.includes(scid)
        && tradingDate.split('T')[0] >= startDate
        && tradingDate.split('T')[0] <= endDate);
  const filteredVersions = [...new Set(filteredOverrideHeaders
    .map(({ version }) => (version)))]
    .map((version) => ({ label: version, value: version }))
    .filter(({ label }) => !!label);

  const orderedVersions = filteredVersions.sort((a, b) => b.value - a.value);

  orderedVersions.push({ value: -1, label: 'MAX' });
  orderedVersions.unshift({ value: 'ALL', label: 'ALL' });

  this.$store.commit('ciso/setParams', ['templateOverrideVersions', orderedVersions]);
  this.$store.commit('ciso/setParams', ['templateOverrideVersionsSelected', -1]);
  return true;
}

export const templateOverrideVersions = {
  label: 'Version',
  value: null,
  type: 'select',
  visible: true,
  options: [],
  watchers: [{
    propertyToWatch: 'tradeDateRangeSelected',
    handler: templateOverrideVersionsHandler,
  }, {
    propertyToWatch: 'marketParticipantsSelected',
    handler: templateOverrideVersionsHandler,
  }, {
    propertyToWatch: 'overrideHeaders',
    handler: templateOverrideVersionsHandler,
  }],
};

export const oasisSnapshotIndicator = {
  label: 'Snapshot Indicator',
  value: null,
  type: 'select',
  options: [{ label: 'T40MIN', value: 'T40MIN' },
    { label: 'T55MIN', value: 'T55MIN' },
    { label: 'T75MIN', value: 'T75MIN' },
  ],
};